import { AuthAPI, AuthConfigResult, UserInfoResult, UserModel } from '@cibo/core'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Location } from 'react-router-dom'
import {
  authDirectLogout,
  selectAuthSessionResolved,
  selectAuthUserEmail,
  selectAuthUserId,
  selectAuthUserIsLoggedIn,
  selectAuthUsername,
  selectOrgAccountId,
  selectOrgName,
  selectUserPersona,
  selectUserRoles,
} from '../../store'

type Props = {
  onLogout?(): void
}

export const useAuth = (props?: Props) => {
  const dispatch = useDispatch()
  const isLoggedIn = useSelector(selectAuthUserIsLoggedIn)
  const authResolved = useSelector(selectAuthSessionResolved)
  const userEmail = useSelector(selectAuthUserEmail)
  const roles = useSelector(selectUserRoles)
  const username = useSelector(selectAuthUsername)
  const persona = useSelector(selectUserPersona)
  const userId = useSelector(selectAuthUserId)
  const orgName = useSelector(selectOrgName)
  const orgId = useSelector(selectOrgAccountId)

  useEffect(() => {
    if (!isLoggedIn) {
      props && props.onLogout && props.onLogout()
    }
  }, [isLoggedIn])

  return {
    authResolved,
    isLoggedIn,
    logout: (location?: Location) => {
      dispatch(authDirectLogout({ location }))
    },
    persona,
    roles,
    userEmail,
    userId,
    username,
    orgName,
    orgId,
  }
}

const useConfig = <R>(select: (data: AuthConfigResult) => R) => {
  const { userId } = useAuth()

  return useQuery<AuthConfigResult, Error, R>({
    queryKey: ['auth-config', userId],
    queryFn: async () => {
      const response: AxiosResponse<AuthConfigResult> = await AuthAPI.config()
      return response.data
    },
    select,
    retry: false,
  })
}

export const useChildOrgList = () => useConfig(data => data.owningOrg?.children)

export const useAccessibleOrgs = () => useConfig(data => data.accessibleSubOrgs)

export const useOwningOrg = () => useConfig(data => data.owningOrg)

export const useUserAuthOrg = () => useConfig(data => data)

export const useUser = () => {
  const { userId } = useAuth()
  return useQuery<UserModel | undefined, Error>({
    queryKey: ['userInfo', userId],
    queryFn: async () => {
      try {
        const response: AxiosResponse<UserInfoResult> = await AuthAPI.userInfo()
        return new UserModel(response.data)
      } catch (error) {
        console.error(JSON.stringify(error))
        return undefined
      }
    },
    retry: false,
  })
}
